/**
 * whiteboardLivingGraph — bridge from an imported whiteboard to the Peak
 * Priorities Visual Language engine's 'living-graph' move.
 *
 * The Visual Language engine (assets/visual-language/engine/) is a static
 * SVG pipeline outside the SPA: each "conceptual move" is a renderer fed by a
 * plain config object, editable live in the viewer at /visual-language/. This
 * module produces that config from a RevealScript, so a board imported from a
 * photo can be pasted straight into the viewer's config editor (select the
 * Living Graph move → Edit Config → paste → Apply) or saved alongside the
 * other move configs.
 *
 * Pure and deterministic; the reveal overlay's diagram view exposes it as
 * "Copy VL config".
 */

import type { RevealScript } from './whiteboardReveal';

/** One box on the living graph, positioned in board space (x/y in [0,1]). */
export interface LivingGraphNode {
  id: string;
  /** The platform item this box committed to — traceability, not rendering. */
  itemId: string;
  label: string;
  x: number;
  y: number;
  category?: string;
  /** Matched to an existing item instead of created (dashed treatment). */
  reused?: boolean;
  /** Renamed/moved by a re-capture (pulse treatment). */
  changed?: boolean;
}

export interface LivingGraphEdge {
  source: string;
  target: string;
  type: string;
  directed: boolean;
}

export interface LivingGraphPayload {
  /** Provenance line rendered in the diagram's Full Detail layer. */
  source: string;
  nodes: LivingGraphNode[];
  edges: LivingGraphEdge[];
}

/**
 * The engine config envelope for the 'living-graph' move. Everything except
 * `title`, `subtitle`, and `graph` matches the registered sample config in
 * assets/visual-language/engine/configs.js — the envelope prose is the move's
 * identity; the payload is the board.
 */
export function livingGraphConfigFromScript(script: RevealScript): Record<string, unknown> {
  const idByItem = new Map<string, string>();
  const nodes: LivingGraphNode[] = script.nodes.map((n, i) => {
    const id = `n${i + 1}`;
    idByItem.set(n.itemId, id);
    return {
      id,
      itemId: n.itemId,
      label: n.title,
      x: n.position.x,
      y: n.position.y,
      ...(n.itemCategory ? { category: n.itemCategory } : {}),
      ...(n.kind === 'reused' ? { reused: true } : {}),
      ...(n.kind === 'changed' ? { changed: true } : {}),
    };
  });

  const edges: LivingGraphEdge[] = script.edges
    .filter((e) => idByItem.has(e.sourceItemId) && idByItem.has(e.targetItemId))
    .map((e) => ({
      source: idByItem.get(e.sourceItemId)!,
      target: idByItem.get(e.targetItemId)!,
      type: e.type,
      directed: e.directionality === 'directed',
    }));

  return {
    id: 'living-graph',
    move: '13',
    title: script.boardTitle,
    tagline: 'A physical whiteboard, absorbed into the system as a living graph.',
    principle:
      'The wall is a projection of the graph, not the other way around. Boxes become Items, arrows become relationships, height becomes altitude — so what the room drew keeps living after the room empties.',
    grammar:
      'Box = Item. Color = category. Arrow = depends on. Dashed connector = prerequisite. Dashed border = reused item. Height = altitude (strategic ↑).',
    color: '#3A7D56',
    file: './viewer.html?id=living-graph',
    hasRenderer: true,
    layerDefs: [
      { label: 'Structure', desc: 'Altitude strata, boxes, and connectors' },
      { label: '+ Context', desc: 'Subtitle and category legend' },
      { label: 'Full Detail', desc: 'Provenance and grammar rules' },
    ],
    subtitle: [
      `${nodes.length} item${nodes.length === 1 ? '' : 's'} and ${edges.length} relationship${
        edges.length === 1 ? '' : 's'
      } captured from the wall — no retyping.`,
      'Positions are the board’s own: strategic intent near the top, execution detail near the bottom.',
    ],
    grammarRules: [
      'Box = Item · Color = category · Arrow = depends on',
      'Dashed connector = prerequisite · Dashed border = reused existing item',
      'Vertical position = altitude — strategic at the top, tactical at the bottom',
    ],
    graph: {
      source: 'Captured from a whiteboard photo',
      nodes,
      edges,
    } satisfies LivingGraphPayload,
  };
}
